import { Link } from "react-router";
import { TabBar } from "../../components/TabBar";
import { ReadinessRing } from "../../components/ReadinessRing";
import { Button } from "../../components/Button";
import { InlineNameEditor } from "../../components/InlineNameEditor";
import { InstallCard } from "../../components/InstallCard";
import { readinessBand } from "../../readinessBand";

export interface SubjectSummary {
  subject: string;
  /** null before any practice — a dashed ring, never a 0%. */
  readiness: number | null;
  confidence: number;
  weakTopics: string[];
  dueCount?: number | undefined;
}

export type HomeState = "normal" | "cold-start" | "trial" | "locked" | "lapsed";

interface HomeScreenProps {
  firstName: string;
  greeting?: string | undefined;
  streakDays: number;
  /** Set when the streak has just been broken, for the honest "was N" line. */
  streakBefore?: number | undefined;
  continueLabel?: string | undefined;
  subjects: SubjectSummary[];
  state?: HomeState | undefined;
  trialUsed?: number | undefined;
  trialTotal?: number | undefined;
  /** Wired by the route; the locked card is the only paid entry point on Home. */
  onUpgrade?: (() => void) | undefined;
  /** Inline name save — lets the student add their name without leaving Home. */
  onSaveName?: ((firstName: string) => Promise<void>) | undefined;
}

/**
 * Screen 15 — Home, with the five states from the state pack folded in.
 *
 * `locked` is **browse-only, never a lockout**: a student who has run out of
 * free questions keeps their scores in full view. Hiding them would punish the
 * person for not paying yet, and the score is the reason they would.
 */
export function HomeScreen({
  firstName,
  greeting = "Good evening",
  streakDays,
  streakBefore,
  continueLabel,
  subjects,
  state = "normal",
  trialUsed,
  trialTotal,
  onUpgrade,
  onSaveName,
}: HomeScreenProps) {
  const isLapsed = state === "lapsed";

  return (
    <div className="app-column">
      <div className="flex-1 px-5 pb-6 pt-6">
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="m-0 text-[13px] text-text-muted">{greeting}</p>
            <p className="m-0 font-[family-name:var(--font-display)] text-[22px] font-bold">
              {firstName}
            </p>
          </div>
          <div className="text-right">
            <span
              className={`tabular rounded-[var(--radius-chip)] border px-2.5 py-1 text-[12px] font-semibold ${
                isLapsed
                  ? "border-border bg-surface text-text-muted"
                  : "border-accent bg-accent-tint text-text"
              }`}
            >
              {streakDays} days
            </span>
            {isLapsed && streakBefore ? (
              <p className="tabular m-0 pt-1 text-[11px] text-text-muted">
                was {streakBefore} before the break
              </p>
            ) : null}
          </div>
        </header>

        {onSaveName ? <InlineNameEditor firstName={firstName} onSave={onSaveName} /> : null}

        <InstallCard />

        {state === "trial" && trialUsed !== undefined && trialTotal !== undefined ? (
          /*
           * The progress bar was the whole card, with nothing to tap.
           *
           * A student watching their free questions run down had no way to pay
           * from here — the only action appeared after they had already run
           * out. Waiting until someone is blocked to show them the price is a
           * worse moment to ask, not a better one.
           */
          <div className="mt-5 rounded-[var(--radius-card)] border border-border bg-surface p-3.5">
            <div className="flex items-baseline justify-between">
              <span className="text-[12.5px] text-text-muted">Free preview</span>
              <span className="tabular text-[12.5px] font-semibold">
                {trialUsed} of {trialTotal}
              </span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-[var(--radius-chip)] bg-surface-high">
              <div
                className="h-full rounded-[var(--radius-chip)] bg-accent"
                style={{ width: `${(trialUsed / trialTotal) * 100}%` }}
              />
            </div>
            <button
              type="button"
              onClick={onUpgrade}
              className="mt-3 min-h-11 w-full rounded-[var(--radius-chip)] border border-accent bg-accent-tint px-3 text-[12.5px] font-semibold text-accent"
            >
              Unlock everything — ₦5,000
            </button>
          </div>
        ) : null}

        {state === "locked" ? (
          <div className="mt-5 flex flex-col gap-2.5 rounded-[var(--radius-card)] border border-accent bg-accent-tint p-4">
            <p className="m-0 text-[13.5px] font-semibold">Your free questions are used up.</p>
            <p className="m-0 text-[12.5px] leading-[1.5] text-text-muted">
              Your scores stay right here. Unlock practice whenever you're ready.
            </p>
            <Button className="text-[13px]" onClick={onUpgrade}>
              See what's included
            </Button>
          </div>
        ) : state === "cold-start" ? (
          <div className="mt-6 flex flex-col items-center gap-3">
            <ReadinessRing percentage={null} />
            <p className="m-0 max-w-[260px] text-center text-[13px] leading-[1.5] text-text-muted">
              Answer a few questions and your readiness appears here.
            </p>
            <Link to="/practice" className="w-full">
              <Button>Start my first session</Button>
            </Link>
          </div>
        ) : (
          <Link
            to="/practice"
            className="mt-5 flex items-center justify-between rounded-[var(--radius-card)] border border-border bg-surface p-4"
          >
            <span className="flex flex-col gap-0.5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
                Continue
              </span>
              <span className="text-[14px]">{continueLabel ?? "Pick up where you left off"}</span>
            </span>
            <span aria-hidden="true" className="text-accent">
              →
            </span>
          </Link>
        )}

        {state !== "cold-start" ? (
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-6">
            {subjects.map((entry) => (
              <li
                key={entry.subject}
                className="flex items-center gap-4 rounded-[var(--radius-card)] border border-border bg-surface p-4"
              >
                <ReadinessRing percentage={entry.readiness} size={64} />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[14px] font-semibold">{entry.subject}</span>
                  {entry.readiness !== null ? (
                    <span className="text-[11.5px] capitalize text-text-muted">
                      {readinessBand(entry.confidence, entry.readiness)}
                    </span>
                  ) : null}
                  <span className="truncate text-[11.5px] text-text-muted">
                    {entry.weakTopics.length > 0
                      ? entry.weakTopics.join(" · ")
                      : "No weak topics yet"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <TabBar />
    </div>
  );
}
